import { RuleFormFormState } from '../../components/Rules/molecules/RuleForm';
import { useRangeCriteria } from './useRangeCriteria';
import { useMeasurementsCriteria } from './useMeasurementsCriteria';
import { CriteriaCreate, RuleDetailResponse } from '../../interfaces/rule';
import {
  isDDUDDP,
  isGeolocation,
  isHsCode,
  isMeasurements,
  isPostalCodeFile,
  isPRC,
  isRangeValue,
  isServiceCode,
  isCountry,
} from '../../utils/Rules/CriteriaValidator';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

export const useRuleAdapter = () => {
  const { handleOnChange } = useRangeCriteria({ first_value: '', second_value: '' });
  const { handleOnChange: measurementOnChange } = useMeasurementsCriteria({
    width: { from: '', to: '' },
    height: { from: '', to: '' },
    length: { from: '', to: '' },
  });

  const countryCode = useSelector((state: RootState) => state.user.user?.company?.country?.code) || '';


  const parseAPIToForm = (apiResponse: RuleDetailResponse): RuleFormFormState => {
    if (!apiResponse.error) {
      const { criterias, name } = apiResponse.groups;
      const criteriasParse = criterias.reduce((acc, cur) => {

        if (isRangeValue(cur.key) && cur.criteria_configurations) {
          const { second_value, first_value } = cur.criteria_configurations;
          return {
            ...acc,
            [cur.id]: { criteriaKey: cur.key, value: { from: first_value, to: second_value }, criteriaId: cur.id },
          };
        }

        if (isMeasurements(cur.key)) {
          const value = cur.criterial_list?.reduce((a, c) => {
            return {
              ...a,
              [c.value + '']: { from: c.first_value, to: c.second_value },
            };
          }, {});

          return {
            ...acc,
            [cur.id]: { criteriaKey: cur.key, value, criteriaId: cur.id },
          };
        }

        if (isServiceCode(cur.key)) {
          const value = cur.criterial_list?.map(i => ({ label: '', value: i.code }));
          return {
            ...acc,
            [cur.id]: { criteriaKey: cur.key, value, criteriaId: cur.id },
          };
        }

        if (isGeolocation(cur.key)) {
          const value = cur.criterial_list?.map(i => ({
            label: i.criteria_group?.name,
            value: i.criteria_group?.name,
          }));
          const apiCountryCode = cur.criterial_list?.[0]?.criteria_group?.country?.code || countryCode;
          return {
            ...acc,
            [cur.id]: {
              criteriaKey: cur.key,
              value: { countryCode: apiCountryCode, values: value },
              criteriaId: cur.id,
            },
          };
        }

        if (isDDUDDP(cur.key) && cur.criteria_configurations) {
          const { first_value } = cur.criteria_configurations;
          return {
            ...acc,
            [cur.id]: { criteriaKey: cur.key, value: first_value === 'true', criteriaId: cur.id },
          };
        }


        if (isPRC(cur.key) && cur.criteria_configurations) {
          const { first_value } = cur.criteria_configurations;
          return {
            ...acc,
            [cur.id]: { criteriaKey: cur.key, value: first_value === 'true', criteriaId: cur.id },
          };
        }

        if (isPostalCodeFile(cur.key) || isHsCode(cur.key)) {
          return {
            ...acc,
            [cur.id]: { criteriaKey: cur.key, value: '', criteriaId: cur.id },
          };
        }

        if (isCountry(cur.key)) {
          // Puede venir como criterial_list (listado) o criteria_configurations (edición)
          let codes: string[] = []
          if (cur.criterial_list && cur.criterial_list.length > 0) {
            codes = cur.criterial_list
              .map((i: { code?: string }) => i.code ?? '')
              .filter((c: string) => c !== '')
          } else if (cur.criteria_configurations?.first_value) {
            codes = (cur.criteria_configurations.first_value as string)
              .split(',')
              .map((c: string) => c.trim())
              .filter((c: string) => c !== '')
          }
          const selectedCountries = codes.map((c: string) => ({ label: c, value: c }))
          return {
            ...acc,
            [cur.id]: {
              criteriaKey: cur.key,
              value: { selectedCountries },
              criteriaId: cur.id,
            },
          }
        }

        return {
          ...acc,
        };
      }, {});

      return {
        name,
        criterias: criteriasParse,
      };
    }
    return {
      name: '',
      criterias: {},
    };
  };

  const parseFormToAPI = (form: RuleFormFormState): CriteriaCreate => {
    const allFormCriteria = Object.values(form.criterias)
      .filter((value) => value !== undefined)
      .filter(item => item.value !== undefined);


    const criterias = allFormCriteria.map((g) => {
      if (g?.value !== undefined) {
        if (typeof g.value === 'object' && 'from' in g.value)
          return {
            criteria_type_id: g.criteriaId,
            ...handleOnChange({ first_value: g.value.from, second_value: g.value.to }),
          };

        if (typeof g.value === 'object' && 'width' in g.value) {
          const { width, height, length } = g.value;
          const data = Object.entries(measurementOnChange({ width, height, length })).map(([key, value]) => ({
            value: key,
            operator: value.operator,
            first_value: value.first_value,
            second_value: value.second_value,
          }));
          return {
            criteria_type_id: g.criteriaId,
            data,
          };
        }

        if (typeof g.value === 'object' && 'countryCode' in g.value) {
          // geolocation: countryCode + values array
          return {
            criteria_type_id: g.criteriaId,
            second_value: g.value.countryCode,
            first_value: (g.value as { countryCode: string; values: { value: string }[] }).values.map(v => v.value).filter(c => c).join(','),
          };
        }

        if (typeof g.value === 'object' && 'selectedCountries' in g.value) {
          // country criteria: comma-separated ISO country codes
          return {
            criteria_type_id: g.criteriaId,
            operator: '=',
            first_value: (g.value as { selectedCountries: { label: string; value: string }[] }).selectedCountries.map(c => c.value).join(','),
            second_value: '',
          };
        }

        if (Array.isArray(g.value)) {
          return {
            criteria_type_id: g.criteriaId,
            operator: '=',
            first_value: g.value.map(v => v.value).join(','),
          };
        }

        if (typeof g.value === 'boolean') {
          return {
            criteria_type_id: g.criteriaId,
            operator: 'boolean',
            first_value: g.value + '',
          };
        }

        return {
          criteria_type_id: g.criteriaId,
          file: g.value,
        };
      }
      return {};
    }).filter(c => c.criteria_type_id !== undefined);
    return { name: (form.name != null) ? form.name : '', description: '', criterias };
  };

  const getAllErrors = (criteriaToCreate: CriteriaCreate) => {
    return criteriaToCreate.criterias.reduce((acc, cur) => {
      return {
        ...acc,
        [cur.criteria_type_id]: cur.error?.join(', '),
      };
    }, {});
  };

  return {
    getAllErrors,
    parseFormToAPI,
    parseAPIToForm,
  };
};
